import { CloseIcon, DeleteIcon, EditIcon } from '@assets/icons';
import { CardButton, IconButton } from '@components/button';
import { Toggle } from '@components/form';
import { Group } from '@components/layout';
import { useConfirmation, useModal } from '@components/modal';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import {
  useDeleteSkillMutation,
  useToggleWorkspaceSkillOverrideMutation,
  type Skill,
} from '@generated/graphql';
import { Box, type SxProps } from '@mui/material';
import posthog from 'posthog-js';
import { ServerTag } from '../../ServerTag';
import { ResourceCard } from '../ResourceCard';
import { SkillCreateModal } from './SkillCreateModal';
import { SkillEditModal } from './SkillEditModal';

type AgentSkillCardProps = {
  skill: Pick<Skill, 'name' | 'skillId' | 'version' | 'enabled' | 'config' | 'description'>;
  workspaceId?: string;
  removable?: boolean;
  editable?: boolean;
  auto?: boolean;
  toggleable?: boolean;
  enabled?: boolean;
  hideTag?: boolean;
  source: 'workspace' | 'organization' | 'user';
  onChange: VoidFunction;
};

export const AgentSkillCard = ({
  skill,
  workspaceId,
  removable = false,
  editable = false,
  toggleable = false,
  hideTag = false,
  enabled = false,
  auto = false,
  source,
  onChange,
}: AgentSkillCardProps) => {
  const { showConfirmation } = useConfirmation();

  const [toggleOverride] = useToggleWorkspaceSkillOverrideMutation();

  const { showModal: showEditSkillModal } = useModal({
    component: SkillEditModal,
    componentProps: { skill, onEdit: onChange },
    modalProps: {
      title: `Edit skill: ${skill.name}`,
    },
  });

  const handleToggleSkill = () =>
    showConfirmation({
      onAffirm: async () => {
        await toggleOverride({
          variables: {
            workspaceId,
            skillId: skill.skillId,
          },
          onCompleted: (data) => {
            const disabled = data?.toggleWorkspaceSkillOverride;
            posthog.capture(
              `workspace:user_${disabled ? 'disable' : 'enable'}-inherited-workspace-skill`,
              {
                workspaceId,
                skillId: skill.skillId,
              },
            );
            onChange?.();
          },
        });
      },
      message: `Are you sure you want to ${enabled ? 'disable' : 'enable'} ${skill.name ? `"${skill.name}"` : 'this agent skill'} for this workspace?`,
    });

  const handleEditSkill = () => {
    return showEditSkillModal();
  };

  const handleRemoveSkill = () =>
    showConfirmation({
      onAffirm: async () => {
        await toggleOverride({
          variables: {
            workspaceId,
            skillId: skill.skillId,
          },
          onCompleted: () => {
            posthog.capture('workspace:user_remove-imported-workspace-skill', {
              workspaceId,
              skillId: skill.skillId,
            });
            onChange?.();
          },
        });
      },
      message: `Are you sure you want to remove ${skill.name ? `"${skill.name}"` : 'this agent skill'} from this workspace?`,
    });

  const [deleteSkillMutation] = useDeleteSkillMutation();
  const handleDeleteSkill = () => {
    showConfirmation({
      onAffirm: async () => {
        await deleteSkillMutation({
          variables: { skillId: skill.skillId },
          update(cache) {
            cache.evict({ id: cache.identify(skill) });
            cache.gc();
          },
          onCompleted: () => {
            posthog.capture('workspace:user_delete-workspace-skill', {
              workspaceId,
              skillId: skill.skillId,
            });
            onChange?.();
          },
        });
      },
      message: `Are you sure you want to delete ${skill.name ? `"${skill.name}"` : 'this agent skill'}?`,
    });
  };

  return (
    <ResourceCard>
      <Group alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="body1" handleOverFlow fitContent color="foreground.paperCard.default">
          {skill.name}
        </Typography>
        {skill.version && (
          <ServerTag label={`v${skill.version}`} color="default" variant="caption2" />
        )}
        {!hideTag && (
          <ServerTag
            label={source}
            color={source}
            variant="caption2"
            sx={{ textTransform: 'capitalize' }}
          />
        )}
        {auto && <ServerTag label="Auto" variant="caption2" />}
      </Group>
      <Group alignItems="center" justifyContent="flex-end" flexShrink={0} gap={1}>
        {toggleable && (
          <Tooltip title={enabled ? 'Disable skill' : 'Enable skill'}>
            <Box>
              <Toggle
                checked={enabled}
                onChange={handleToggleSkill}
                sx={{ '&.MuiFormControlLabel-root': { height: '28px !important' } }}
              />
            </Box>
          </Tooltip>
        )}
        {editable && (
          <>
            <IconButton
              Icon={EditIcon}
              tooltip="Details"
              onClick={handleEditSkill}
              size={16}
              variant="default"
            />
            <IconButton
              Icon={DeleteIcon}
              tooltip="Delete"
              onClick={handleDeleteSkill}
              size={16}
              variant="error"
            />
          </>
        )}
        {removable && (
          <IconButton
            Icon={CloseIcon}
            tooltip="Remove"
            onClick={handleRemoveSkill}
            size={16}
            variant="error"
          />
        )}
      </Group>
    </ResourceCard>
  );
};

export const AddAgentSkillCard = ({
  workspaceId,
  sx,
  onCreate,
}: {
  workspaceId: string;
  onCreate: VoidFunction;
  sx?: SxProps;
}) => {
  const { showModal } = useModal({
    component: SkillCreateModal,
    componentProps: {
      fixedScopeType: 'workspace',
      fixedWorkspaceId: workspaceId,
      onCreate,
    },
    modalProps: { title: 'Create workspace agent skill' },
  });

  const handleManageAgentSkills = () => {
    void showModal();
  };

  return (
    <CardButton
      label="Add workspace agent skill"
      onClick={handleManageAgentSkills}
      background="paperCard"
      sx={{
        maxWidth: '100%',
        minWidth: '200px',
        ...sx,
      }}
    />
  );
};

export const ImportAgentSkillCard = ({ onClick, sx }: { onClick: () => void; sx?: SxProps }) => {
  return (
    <CardButton
      label="Manage imported agent skills"
      onClick={onClick}
      background="paperCard"
      sx={{
        width: '100%',
        maxWidth: '800px',
        minWidth: '200px',
        ...sx,
      }}
    />
  );
};
